import React, { useMemo, useState } from "react";
import HealthCard from "./HealthCard";
import { formatNumber } from "../utils";

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const daysPastDue = (nextExecutionDate) => {
  if (!nextExecutionDate) return 0;
  const due = new Date(nextExecutionDate);
  if (Number.isNaN(due.getTime())) return 0;
  const now = new Date();
  const ms = now.setHours(0, 0, 0, 0) - due.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor(ms / 86400000));
};

/**
 * fmd/25 shape:
 * {
 *   "17 44518": [{ plateNumber, itemName, nextExecutionDate, nextOdometerReading, attributes.currentOdo, ... }],
 *   "BFM 000":  [...]
 * }
 * One card row per plate; detail uses days overdue + km past nextOdo when positive.
 */
const normalizeVehicles = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    if (Array.isArray(data?.vehicles)) return data.vehicles;
    if (Array.isArray(data)) return data;
    return [];
  }

  return Object.entries(data)
    .map(([plateKey, items]) => {
      const list = Array.isArray(items) ? items : [];
      if (!list.length) return null;

      const sorted = [...list].sort(
        (a, b) => daysPastDue(b.nextExecutionDate) - daysPastDue(a.nextExecutionDate),
      );
      const primary = sorted[0];
      const overdueDays = daysPastDue(primary.nextExecutionDate);
      const nextDue = formatDate(primary.nextExecutionDate);
      const extraCount = sorted.length - 1;
      const makeModel = [primary.attributes?.make, primary.attributes?.model]
        .filter(Boolean)
        .join(" ");

      const currentOdo = Number(
        primary.attributes?.currentOdo ?? primary.currentOdo ?? 0,
      );
      const nextOdo = Number(primary.nextOdometerReading ?? 0);
      const odoPastDue = currentOdo - nextOdo;

      const detailParts = [];
      if (overdueDays > 0) {
        detailParts.push(`${formatNumber(overdueDays)} days past due`);
      } else if (nextDue) {
        detailParts.push(`due ${nextDue}`);
      }
      if (odoPastDue > 0) {
        detailParts.push(`${formatNumber(odoPastDue)} km past interval`);
      }
      if (makeModel) detailParts.push(makeModel);
      if (extraCount > 0) detailParts.push(`+${extraCount} more`);

      return {
        plate: primary.plateNumber || plateKey,
        reason: primary.itemName || primary.itemCode || "Service overdue",
        detail: detailParts.join(" · "),
        severity: overdueDays >= 30 ? "critical" : "attention",
        vehicleId: primary.vehicleId,
        items: sorted,
        raw: primary,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const aDays = daysPastDue(a.raw?.nextExecutionDate);
      const bDays = daysPastDue(b.raw?.nextExecutionDate);
      return bDays - aDays;
    });
};

const ImmediateAttention = ({
  translate,
  data,
  loading,
  onBookService,
  onViewAll,
}) => {
  const [search, setSearch] = useState("");
  const vehicles = useMemo(() => normalizeVehicles(data), [data]);
  const criticalCount = vehicles.filter(
    (item) => (item.severity || "").toLowerCase() === "critical",
  ).length;

  const filteredVehicles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return vehicles;
    return vehicles.filter((item) =>
      [item.plate, item.reason, item.detail]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [vehicles, search]);

  return (
    <HealthCard
      className="fh-span-5"
      title={translate("vehiclesRequiringImmediateAttention")}
      subtitle={translate("vehiclesRequiringImmediateAttentionSub")}
      badge={
        vehicles.length
          ? `${vehicles.length} ${translate("total")} · ${criticalCount} ${translate("critical")}`
          : undefined
      }
      badgeStyle={{ background: "#fdeaea", color: "#e0484c" }}
      headerAction={
        vehicles.length ? (
          <input
            className="fh-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={translate("searchPlate") || "Search plate..."}
          />
        ) : null
      }
      loading={loading}
    >
      {filteredVehicles.length ? (
        <div className="fh-veh-list" style={{ paddingRight: 4 }}>
          {filteredVehicles.map((item, index) => {
            const severity = (item.severity || "").toLowerCase();
            return (
              <div className="fh-veh" key={`${item.plate}-${index}`}>
                <div
                  className="fh-sev"
                  style={{
                    background: severity === "critical" ? "#e0484c" : "#ef8b3c",
                  }}
                />
                <div className="fh-plate">{item.plate || item.plateNo}</div>
                <div style={{ flex: 1, fontSize: 11.5 }}>
                  {item.reason || item.title}
                  <span
                    style={{
                      display: "block",
                      color: "#8a97a3",
                      fontSize: 10.5,
                    }}
                  >
                    {item.detail || item.description}
                  </span>
                </div>
                <button
                  type="button"
                  className="fh-btn sm"
                  onClick={() => onBookService && onBookService(item)}
                >
                  {translate("bookService")}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="fh-empty">
          {search.trim()
            ? translate("noPlatesMatch")
            : translate("noRecordFound")}
        </div>
      )}
    </HealthCard>
  );
};

export default ImmediateAttention;
